/**
 * Placement Presets - Single Source of Truth
 *
 * Consolidated placement definitions that combine:
 * - UI labels (from constants.ts)
 * - Relative positions (from placements.config.ts)
 * - Physical dimensions (from placement.mapper.ts)
 *
 * All other files should import from here and extract what they need.
 */

import type { PlacementPreset } from './types.js';
import { relative, mm } from './types.js';

// ═══════════════════════════════════════════════════════════════════════════
// FRONT PLACEMENTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Large centered front print
 * - Covers most of the chest area
 * - Good for artwork, large graphics
 */
export const FRONT_CENTER_LARGE: PlacementPreset = {
  id: 'front_center_large',
  name: 'Large Front Print',
  description: 'Centered, approx. 30×40 cm',
  icon: '📐',

  // Contain box: 85% width, 90% height
  maxWidth: relative(0.85),
  maxHeight: relative(0.9),

  // Position: horizontally centered, vertically at 40% — a large print sits
  // above the geometric centre of the print area, closer to the chest.
  positionX: relative(0.5),
  positionY: relative(0.4),

  // External API dimensions for Mockup Factory
  externalApi: {
    designWidthMm: 300,
    designHeightMm: 400,
    designYTopMm: 60,
    designXCenterMm: 0,
  },

  overflow: 'clamp',
};

/**
 * Small centered front print
 * - Upper chest area
 * - Good for small logos, badges
 */
export const FRONT_CENTER_SMALL: PlacementPreset = {
  id: 'front_center_small',
  name: 'Small Chest Center',
  description: 'Above chest, approx. 10×10 cm',
  icon: '🎯',

  // Contain box: 35% width, 35% height
  maxWidth: relative(0.35),
  maxHeight: relative(0.35),

  // Position: horizontally centered, vertically at 15% (upper chest)
  positionX: relative(0.5),
  positionY: relative(0.15),

  // External API dimensions
  externalApi: {
    designWidthMm: 100,
    designHeightMm: 100,
    designYTopMm: 50,
    designXCenterMm: 0,
  },

  overflow: 'clamp',
};

/**
 * Left chest pocket area
 * - Classic logo placement
 * - Small, positioned on left side
 */
export const FRONT_LEFT_CHEST: PlacementPreset = {
  id: 'front_left_chest',
  name: 'Left Chest',
  description: 'Classic logo spot, approx. 8×8 cm',
  icon: '📍',

  // Contain box: 80mm x 80mm (fixed size)
  maxWidth: mm(80),
  maxHeight: mm(80),

  // Position: the wearer's left, which is the viewer's right on a front
  // view — 75% from the left edge, 12% from the top. "Left chest" is named
  // from the wearer's perspective, as in print production.
  positionX: relative(0.75),
  positionY: relative(0.12),

  // External API dimensions
  externalApi: {
    designWidthMm: 80,
    designHeightMm: 80,
    designYTopMm: 60,
    designXCenterMm: 100,
  },

  overflow: 'clamp',
};

/**
 * Right chest logo placement
 * - Horizontal mirror of FRONT_LEFT_CHEST
 * - Same contain box, mirrored position and external X offset
 */
export const FRONT_RIGHT_CHEST: PlacementPreset = {
  id: 'front_right_chest',
  name: 'Right Chest',
  description: 'Mirrored logo spot, approx. 8×8 cm',
  icon: '📍',

  // Contain box: 80mm x 80mm (fixed size)
  maxWidth: mm(80),
  maxHeight: mm(80),

  // Position: the wearer's right, the viewer's left — 25% from the left
  // edge, 12% from the top (mirror of FRONT_LEFT_CHEST).
  positionX: relative(0.25),
  positionY: relative(0.12),

  // External API dimensions
  externalApi: {
    designWidthMm: 80,
    designHeightMm: 80,
    designYTopMm: 60,
    designXCenterMm: -100,
  },

  overflow: 'clamp',
};

// ═══════════════════════════════════════════════════════════════════════════
// BACK PLACEMENTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Large centered back print
 * - Main back design area
 * - Good for artwork, large graphics
 */
export const BACK_CENTER_LARGE: PlacementPreset = {
  id: 'back_center_large',
  name: 'Large Back Print',
  description: 'Centered, approx. 30×40 cm',
  icon: '📐',

  // Contain box: 85% width, 90% height
  maxWidth: relative(0.85),
  maxHeight: relative(0.9),

  // Position: horizontally centered, vertically at 45%
  positionX: relative(0.5),
  positionY: relative(0.45),

  // External API dimensions
  externalApi: {
    designWidthMm: 300,
    designHeightMm: 400,
    designYTopMm: 70,
    designXCenterMm: 0,
  },

  overflow: 'clamp',
};

/**
 * Upper back / neck area
 * - Below collar
 * - Good for small designs, tags
 */
export const BACK_UPPER: PlacementPreset = {
  id: 'back_upper',
  name: 'Upper Back / Neck',
  description: 'Below collar, approx. 10×10 cm',
  icon: '🔝',

  // Contain box: 35% width, 35% height
  maxWidth: relative(0.35),
  maxHeight: relative(0.35),

  // Position: horizontally centered, vertically at 8% (near top)
  positionX: relative(0.5),
  positionY: relative(0.08),

  // External API dimensions
  externalApi: {
    designWidthMm: 100,
    designHeightMm: 100,
    designYTopMm: 40,
    designXCenterMm: 0,
  },

  overflow: 'clamp',
};

/**
 * Lower back area
 * - Above hem
 * - Good for wide designs
 */
export const BACK_LOWER: PlacementPreset = {
  id: 'back_lower',
  name: 'Lower Back',
  description: 'Above hem, approx. 15×8 cm',
  icon: '⬇️',

  // Contain box: 50% width, 30% height
  maxWidth: relative(0.5),
  maxHeight: relative(0.3),

  // Position: horizontally centered, vertically at 85% (near bottom)
  positionX: relative(0.5),
  positionY: relative(0.85),

  // External API dimensions
  externalApi: {
    designWidthMm: 150,
    designHeightMm: 80,
    designYTopMm: 280,
    designXCenterMm: 0,
  },

  overflow: 'clamp',
};

// ═══════════════════════════════════════════════════════════════════════════
// PRESET REGISTRY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * All available presets indexed by ID
 */
export const PRESETS: Record<string, PlacementPreset> = {
  front_center_large: FRONT_CENTER_LARGE,
  front_center_small: FRONT_CENTER_SMALL,
  front_left_chest: FRONT_LEFT_CHEST,
  front_right_chest: FRONT_RIGHT_CHEST,
  back_center_large: BACK_CENTER_LARGE,
  back_upper: BACK_UPPER,
  back_lower: BACK_LOWER,
};

/**
 * All front placement presets
 */
export const FRONT_PRESETS: PlacementPreset[] = [
  FRONT_CENTER_LARGE,
  FRONT_CENTER_SMALL,
  FRONT_LEFT_CHEST,
  FRONT_RIGHT_CHEST,
];

/**
 * All back placement presets
 */
export const BACK_PRESETS: PlacementPreset[] = [
  BACK_CENTER_LARGE,
  BACK_UPPER,
  BACK_LOWER,
];

/**
 * Get a placement preset by ID
 *
 * @param id - The preset ID (e.g., 'front_center_large')
 * @returns The preset, or a default fallback
 */
export function getPlacementPreset(id: string): PlacementPreset {
  const preset = PRESETS[id];
  if (preset) {
    return preset;
  }

  // Fallback: centered large (most common default)
  // Note: Caller should handle unknown presets if warning is needed
  return FRONT_CENTER_LARGE;
}

/**
 * Check if a preset ID is valid
 */
export function isValidPresetId(id: string): boolean {
  return id in PRESETS;
}

/**
 * Get all valid preset IDs
 */
export const VALID_PRESET_IDS = Object.keys(PRESETS);
